import React, { useState, useEffect } from 'react'
import { MenuItem, Position, Checkbox } from '@blueprintjs/core'
import { Select2, ItemListPredicate } from '@blueprintjs/select'
import { Button, IconButton } from '..'
import { IUIButtonProps } from '../buttons/Button'
import fuzzy from 'fuzzaldrin-plus'
import ClearableSelectWrapper from './ClearableSelectWrapper'
import { Flex, HighlightedText } from '@ui'
import api from '@api'

interface EmployeeTitleSelectProps {
  value: string | null
  clearable?: boolean
  onChange: (title: string | null) => void
  buttonProps?: IUIButtonProps
  btnText?: string
  itemDisabled?: (item: string) => boolean
  multi?: boolean
}

const TitleSelect = Select2<string>

export default function EmployeeTitleSelectWrapper({
  value,
  clearable,
  onChange,
  buttonProps,
  btnText,
  itemDisabled,
  multi,
}: EmployeeTitleSelectProps) {
  const titles: string[] = useTitles()

  return (
    <ClearableSelectWrapper>
      <TitleSelect
        resetOnSelect
        popoverProps={{
          position: Position.BOTTOM,
          modifiers: { arrow: { enabled: false } },
          minimal: true,
        }}
        noResults={<MenuItem disabled text="No results" />}
        items={titles}
        itemRenderer={(title, { handleClick, modifiers, query }) => {
          return modifiers.matchesPredicate ? (
            <MenuItem
              data-item={title}
              active={modifiers.active}
              key={title}
              onClick={(e: React.MouseEvent<HTMLElement>) => {
                if (multi) {
                  e.preventDefault()
                  onChange(title)
                  return
                }
                handleClick(e)
              }}
              text={
                <Flex>
                  {multi && <Checkbox checked={itemDisabled && itemDisabled(title)} />}
                  <HighlightedText text={title} query={query} />
                </Flex>
              }
            />
          ) : null
        }}
        itemListPredicate={filterTitles}
        onItemSelect={onChange}
      >
        <Button
          {...buttonProps}
          rightIconName={value ? undefined : btnText ? undefined : 'chevron-down'}
          text={value || btnText || 'Select'}
        />
      </TitleSelect>
      {value && clearable && (
        <IconButton
          minimal
          iconName="times"
          tooltip="Clear"
          data-button="clear-button"
          onClick={() => onChange(null)}
        />
      )}
    </ClearableSelectWrapper>
  )
}

const filterTitles: ItemListPredicate<string> = (query, titles) => {
  return query ? fuzzy.filter(titles, query) : titles
}

let persistedState: string[] = []

export function useTitles(): string[] {
  const [employeeTitles, setTitles] = useState<string[]>(persistedState)

  useEffect(() => {
    api.company.employeeTitles().then((employeeTitles) => setTitles(employeeTitles))
  }, [])

  useEffect(() => {
    persistedState = employeeTitles
  })

  return employeeTitles
}
